import ScrollItem from "./ScrollItem";
import m, {Vnode, VnodeDOM} from "mithril";
import {closestNumberIndex} from "../../helpers"
import Cell from "../../../../model/form/cell";

export default class HorizontalScrollPicker {

    private static readonly SCALE_DOWN_FACTOR = 3;
    private static readonly OPACITY_FACTOR = 10;
    private static readonly OPACITY_MIN = 0.1;

    private isCentered = false;
    private _currentIndex: number|null = null;

//  |----------------------|
//  |  00  01  02  03  04  | <-- $scrollableArea, items
//  |----------==----------|
//             /\            <- currentIndex => 2
//  |______________________| <- scrollableAreaWidth
//  |____|                   <- itemOffsets[1]        offset inside scrollableArea
//  |__________|             <- scrollItemOffsets[0]  scroll offset to center item
//   |____|                  <- itemWidths[0]         item width includes padding

    private itemOffsets: number[]|null = null;
    private scrollItemOffsets: number[]|null = null;
    private itemWidths: number[]|null = null;
    private scrollableAreaWidth: number = 0;
    private $scrollableArea!: JQuery;

    private alignItemToCenterTimeout: number|null = null;
    private allowAlignToCenter = true;

    private draggingInfo = {
        startx: 0,
        starty: 0,
        diffx: 0,
        diffy: 0,
        drag: false,
    };

    constructor (
        private readonly items: ScrollItem[],
        public readonly cell: Cell,
        private value: string|null = null,
        private readonly onSelectItem: (item: ScrollItem) => void,
        private readonly onScrollOverItem: ((item: ScrollItem) => void)|null = null,
        private readonly initIndex:number = 0,
    ) {
        for (let i=0; i<items.length; i++) {
            if (this.value === this.items[i].value) {
                this._currentIndex = i;
                break;
            }
        }
    }

    //-----------------/
    // HANDLERS
    //
    private createOnClickItem(index: number) {
        return () => {
            this.scrollToItem(index);
        }
    }

    private onScroll() {
        if (this.itemOffsets === null) {
            // It should never happen
            console.log('Never trust comments. The comment lies, it just happened!');
            return;
        }
        const scrollLeft = this.$scrollableArea.scrollLeft();
        const scroll = scrollLeft + this.scrollableAreaWidth * 0.5;
        const items = this.$scrollableArea.find('.item');
        for (let i = 0; i < items.length; i++) {
            if (Math.abs(this.itemOffsets[i] - scroll) > this.scrollableAreaWidth) {
                continue;
            }
            const item = items[i];
            const center = scroll - item.offsetWidth * 0.5;
            const deltaRelative = Math.abs(item.offsetLeft - center) / this.scrollableAreaWidth * 0.5;
            const scale = Math.max(1 - deltaRelative * HorizontalScrollPicker.SCALE_DOWN_FACTOR, 0.01);
            const opacity = Math.max(1 - deltaRelative * HorizontalScrollPicker.OPACITY_FACTOR, HorizontalScrollPicker.OPACITY_MIN);
            item.style.transform = `scale(${scale})`;
            item.style.opacity = '' + opacity;
        }
        this.currentIndex = this.getIndexFromOffset(scrollLeft);

        this.isCentered = false;
        this.announceScrolling();
    }

    // https://medium.com/creative-technology-concepts-code/native-browser-touch-drag-using-overflow-scroll-492dc92ac737
    private onMouseDown(e: MouseEvent) {
        if (!this.$scrollableArea) {
            return;
        }
        this.draggingInfo.startx = e.clientX + this.$scrollableArea.scrollLeft();
        this.draggingInfo.starty = e.clientY + this.$scrollableArea.scrollTop();
        this.draggingInfo.diffx = 0;
        this.draggingInfo.diffy = 0;
        this.draggingInfo.drag = true;
    }

    private onMouseMove(e: MouseEvent) {
        if (this.draggingInfo.drag) {
            this.draggingInfo.diffx = (this.draggingInfo.startx - (e.clientX + this.$scrollableArea.scrollLeft() ));
            this.draggingInfo.diffy = (this.draggingInfo.starty - (e.clientY + this.$scrollableArea.scrollTop() ));
            this.$scrollableArea.scrollLeft(this.$scrollableArea.scrollLeft() + this.draggingInfo.diffx);
            this.$scrollableArea.scrollTop(this.$scrollableArea.scrollTop() + this.draggingInfo.diffy);
        }
    }

    private onMouseUp(e: MouseEvent) {
        this.draggingInfo.drag = false;
        let start = 1;
        let animationRequest: number|null = null;

        const animate = () => {
            let step = Math.sin(start);
            if (step <= 0 && animationRequest !== null) {
                window.cancelAnimationFrame(animationRequest);
            } else {
                this.$scrollableArea.scrollLeft( this.$scrollableArea.scrollLeft() + this.draggingInfo.diffx * step );
                this.$scrollableArea.scrollTop(this.$scrollableArea.scrollTop() + this.draggingInfo.diffy * step);
                start -= 0.02;
                animationRequest = window.requestAnimationFrame(animate);
            }
        };
        animate();
    }


    //------------------/
    // LOGIC
    //
    private getOffsetScrollOfItem(index: number): number {
        if (this.itemOffsets && this.itemOffsets[index] !== undefined) {
            return this.itemOffsets[index];
        }
        return 0;
    }

    private getWidthOfItem(index: number): number {
        if (this.itemWidths && this.itemWidths[index] !== undefined) {
            return this.itemWidths[index];
        }
        return 0;
    }

    private refreshItemsPlacementInfo(vnode: VnodeDOM) {
        this.scrollableAreaWidth = this.$scrollableArea.width();

        this.itemOffsets = [];
        this.scrollItemOffsets = [];
        this.itemWidths = [];

        const items = $(vnode.dom).find('.item');
        for (let i= 0; i<items.length; i++) {
            this.itemWidths[i] = items[i].offsetWidth;
            this.itemOffsets[i] = items[i].offsetLeft;
            this.scrollItemOffsets[i] = this.itemOffsets[i] + (this.itemWidths[i] - this.scrollableAreaWidth)*0.5;
        }
        if (this.currentIndex !== null) {
            // scroll on item ofter load or resize window
            this.scrollToItem(this.currentIndex, 1);
            this.onScroll();
        } else {
            this.$scrollableArea.scrollLeft(this.getTargetOffset(this.initIndex));
        }
    }

    private scrollToItem(index: number, duration:number = -1): void {
        this.onSelectItem(this.items[index]);
        const targetOffset = this.getTargetOffset(index);

        if (duration === 0) {
            this.$scrollableArea.scrollLeft(targetOffset);
            this.isCentered = true;
            return;
        }

        if (duration < 0) {
            duration = Math.min(Math.max( Math.abs(this.$scrollableArea.scrollLeft() - targetOffset)/0.5, 100), 400);
        }

        this.$scrollableArea.animate({scrollLeft: targetOffset}, duration, () => {
            this.isCentered = true;
        });
    }

    private getTargetOffset(index: number): number {
        return this.getOffsetScrollOfItem(index) + (this.getWidthOfItem(index) - this.scrollableAreaWidth)*0.5;
    }

    private getIndexFromOffset(offset: number) {
        return this.scrollItemOffsets ? closestNumberIndex(offset, this.scrollItemOffsets) : 0;
    }

    private alignItemToCenter(): void {
        if (!this.isCentered && this.currentIndex !== null) {
            this.scrollToItem(this.currentIndex);
        }
    }

    private announceScrolling(): void {
        if (this.alignItemToCenterTimeout) {
            window.clearTimeout(this.alignItemToCenterTimeout);
        }
        if (this.allowAlignToCenter && !this.isCentered) {
            this.alignItemToCenterTimeout = window.setTimeout(() => {
                this.alignItemToCenter();
            }, 500);
        }
    }

    private set currentIndex(index: number|null) {
        if (this.onScrollOverItem && index !== this._currentIndex && index !== null) {
            this.onScrollOverItem(this.items[index]);
        }
        this._currentIndex = index;
    }

    private get currentIndex() {
        return this._currentIndex;
    }

    private searchItemIndex(label: string): number|undefined {
        let index = undefined;
        this.items.some((item,i) => {
            if (item.label === label) {
                index = i;
                return true; // break;
            }
            return false; // continue;
        });
        return index;
    }

    private prevItem(): number {
        const newIndex = Math.min(this.items.length-1, Math.max(0, (this.currentIndex || this.initIndex)-1) );
        this.scrollToItem(newIndex);
        this.onSelectItem(this.items[newIndex]);
        return newIndex;
    }

    private nextItem(): number {
        const newIndex = Math.min(this.items.length-1, Math.max(0, (this.currentIndex || this.initIndex)+1) );
        this.scrollToItem(newIndex);
        this.onSelectItem(this.items[newIndex]);
        return newIndex;
    }

    //-----------------/
    // VIEW
    //
    public view(): Vnode {

        const items = this.items.map( (v, i) =>  this.viewItem(i) );

        return m('.horizontalScrollPicker'+(this.cell.enabled ? '' : '.disabled')+(this.isCentered ? '.isCentered' : '')+(this.currentIndex!==null ? '.isSelected' : ''), {}, [
            m('.controlLeft', {onclick: ()=>this.prevItem()}, [
                m('i.icon.icon-chevron-left'),
            ]),
            m('.scrollableArea', {
                onscroll: this.onScroll.bind(this),
                onmouseover: () => {this.allowAlignToCenter = false},
                onmouseleave: () => {
                    this.allowAlignToCenter = true;
                    this.draggingInfo.drag = false;
                    this.announceScrolling();
                },
                ontouchstart: () => {this.allowAlignToCenter = false},
                ontouchend: () => {
                    this.allowAlignToCenter = true;
                    this.announceScrolling();
                },
                onmousedown: this.onMouseDown.bind(this),
                onmousemove: this.onMouseMove.bind(this),
                onmouseup: this.onMouseUp.bind(this),
            }, [
                m('.items', {}, items)
            ]),
            m('input.direct-input', {
                oninput: (e: InputEvent) => {
                    const i = this.searchItemIndex( (<HTMLInputElement>e.currentTarget).value );
                    if (typeof i === "number") {
                        this.scrollToItem(i);
                        this.onSelectItem(this.items[i]);
                    }
                },
                onblur: (e: InputEvent) => (<HTMLInputElement>e.currentTarget).value = '',
                onkeydown: (e: KeyboardEvent) => {
                    if (e.key === "ArrowUp" || e.key === "ArrowDown" || e.key === "ArrowLeft" || e.key === "ArrowRight") {
                        e.preventDefault();
                        let i = this.searchItemIndex((<HTMLInputElement>e.currentTarget).value);
                        if (i !== undefined) {
                            i += (e.key === "ArrowUp" || e.key === "ArrowRight") ? 1 : -1;
                            i = Math.min(this.items.length-1, Math.max(0, i) );
                            (<HTMLInputElement>e.currentTarget).value = ''+i;
                            this.scrollToItem(i);
                            this.onSelectItem(this.items[i]);
                        }
                    }
                }
            }),
            m('.controlRight', {onclick: ()=>this.nextItem()}, [
                m('i.icon.icon-chevron-right'),
            ]),
        ]);
    }


    private viewItem(index: number): Vnode {
        return m('.item', {
            onclick: this.createOnClickItem(index).bind(this)
        }, m.trust(this.items[index].label));
    }

    public oncreate(vnode: VnodeDOM): void {
        this.$scrollableArea = $(vnode.dom).find('.scrollableArea');
        this.refreshItemsPlacementInfo(vnode);
        $(window).on('resize', () => {
            this.refreshItemsPlacementInfo(vnode);
        });
    }
}